import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ListStore } from '@list-store';
import { ConversationMessageView, ConversationParticipantView } from '@mark43/cad-api';
import { useCallback } from 'react';
import { ConversationKey } from '../../models';
import { useMessagingStore } from '../use-messaging-store';
import { useMessagingResource } from './use-messaging-resource';

export type AddMessageParams = {
    isUrgent: boolean;
    senderParticipant: ConversationParticipantView;
    text: string;
    conversationKey: ConversationKey;
};

const getQueryKey = (conversationKey: ConversationKey) => {
    return ['conversation-messages', conversationKey.toString()];
};

export const useAddMessage = () => {
    const queryClient = useQueryClient();
    const { sendMessage } = useMessagingResource();
    const { setConversationScrollToIndex } = useMessagingStore();

    const addMessageToStore = useCallback(
        (message: ConversationMessageView, conversationKey?: ConversationKey) => {
            if (conversationKey === undefined) {
                throw new Error('ConversationKey is undefined');
            }

            const queryKey = getQueryKey(conversationKey);
            const queryData = queryClient.getQueryData<{
                messageList: ListStore<ConversationMessageView>;
            }>(queryKey);

            if (queryData === undefined || queryData.messageList === undefined) {
                return;
            }

            const { messageList } = queryData;

            messageList.upsertItems([
                {
                    ...message,
                    isSender: true,
                },
            ]);

            // scroll to the message after being added
            const addedMessageIndex = messageList.getItemIndex(message.id);
            if (addedMessageIndex !== undefined) {
                setConversationScrollToIndex(conversationKey, addedMessageIndex);
            }

            // no mutation of querydata allowed so we create a new object
            // see here https://tanstack.com/query/v4/docs/react/guides/updates-from-mutation-responses#immutability
            queryClient.setQueryData(queryKey, { messageList });
        },
        [queryClient, setConversationScrollToIndex]
    );

    const mutation = useMutation<ConversationMessageView, unknown, AddMessageParams, unknown>({
        mutationFn: async ({
            isUrgent,
            text,
            senderParticipant,
            conversationKey: selectedConversationKey,
        }) => {
            const message = await sendMessage({
                conversationKey: selectedConversationKey,
                isUrgent,
                text,
                senderParticipant,
            });
            return message;
        },
        onSuccess: (message, { conversationKey: selectedConversationKey }) =>
            addMessageToStore(message, selectedConversationKey),
    });

    const addMessage = ({
        isUrgent,
        text,
        senderParticipant,
        conversationKey,
    }: AddMessageParams) => {
        return mutation.mutateAsync({
            isUrgent,
            text,
            senderParticipant,
            conversationKey,
        });
    };

    const addCreateConversationMessage = (
        createdMessage: ConversationMessageView,
        conversationKey: ConversationKey
    ) => {
        const queryKey = getQueryKey(conversationKey);

        queryClient.setQueryData(queryKey, {
            messageList: new ListStore<ConversationMessageView>(),
        });
        addMessageToStore(createdMessage, conversationKey);
    };

    return { addMessage, addCreateConversationMessage };
};
